// Placeholder image utilities for artists without custom images
// Uses deterministic selection based on artist ID for consistency
// Images are curated from Unsplash for a professional look

// Professional cover images - music/performance themed (Unsplash photo IDs)
// These are concert halls, stages, lighting, and music-themed backgrounds
const COVER_IMAGES = [
  'photo-1493225457124-a3eb161ffa5f', // Concert crowd with purple lights
  'photo-1514525253161-7a46d19cd819', // DJ/stage with blue lights
  'photo-1501386761578-eac5c94b800a', // Concert crowd silhouette
  'photo-1470229722913-7c0e2dbbafd3', // Stage with dramatic lighting
  'photo-1459749411175-04bf5292ceea', // Concert with colorful lights
  'photo-1524368535928-5b5e00ddc76b', // Microphone close-up
  'photo-1493676304819-0d7a8d026dcf', // DJ mixing board
  'photo-1506157786151-b8491531f063', // Festival crowd
  'photo-1598387993441-a364f854c3e1', // Neon music vibes
  'photo-1571266028243-d220c6a8db6f', // Guitar on stage
] as const

// Professional profile images - abstract/artistic that work as avatars
// These are abstract patterns, gradients, and artistic shots that look good circular
const PROFILE_IMAGES = [
  'photo-1557672172-298e090bd0f1', // Abstract purple gradient
  'photo-1579546929518-9e396f3cc809', // Colorful gradient waves
  'photo-1557682250-33bd709cbe85', // Pink/purple abstract
  'photo-1558591710-4b4a1ae0f04d', // Neon abstract
  'photo-1618005198919-d3d4b5a92ead', // Modern gradient art
  'photo-1620641788421-7a1c342ea42e', // Geometric abstract
  'photo-1634017839464-5c339bbe3c5c', // Soft gradient
  'photo-1553356084-58ef4a67b2a7', // Purple abstract paint
  'photo-1563089145-599997674d42', // Colorful smoke
  'photo-1549317661-bd32c8ce0db2', // Abstract light trails
] as const

// Fallback gradients if images fail to load
const COVER_GRADIENTS = [
  { from: '#4f46e5', via: '#7c3aed', to: '#a855f7' }, // Indigo to purple
  { from: '#0891b2', via: '#0e7490', to: '#155e75' }, // Cyan to teal
  { from: '#dc2626', via: '#ea580c', to: '#f97316' }, // Red to orange
  { from: '#059669', via: '#10b981', to: '#34d399' }, // Emerald gradient
  { from: '#7c3aed', via: '#c026d3', to: '#e879f9' }, // Purple to fuchsia
  { from: '#1d4ed8', via: '#3b82f6', to: '#60a5fa' }, // Blue gradient
  { from: '#b91c1c', via: '#be123c', to: '#db2777' }, // Red to pink
  { from: '#0369a1', via: '#0284c7', to: '#0ea5e9' }, // Sky blue gradient
  { from: '#4338ca', via: '#6366f1', to: '#818cf8' }, // Indigo gradient
  { from: '#15803d', via: '#16a34a', to: '#22c55e' }, // Green gradient
] as const

/**
 * Simple hash function to convert string to number
 * Ensures deterministic results for the same input
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

/**
 * Get a deterministic index (0-9) based on artist ID
 */
function getPlaceholderIndex(artistId: string): number {
  return hashString(artistId) % 10
}

/**
 * Get placeholder profile image URL for an artist
 * Uses curated Unsplash images for a professional look
 */
export function getPlaceholderProfileUrl(artistId: string, _artistName?: string): string {
  const index = getPlaceholderIndex(artistId)
  const photoId = PROFILE_IMAGES[index]
  
  // Unsplash image URL with cropping for avatar use
  // 400x400 is good quality for most avatar sizes
  return `https://images.unsplash.com/${photoId}?w=400&h=400&fit=crop&crop=center&auto=format&q=80`
}

/**
 * Get placeholder cover image CSS gradient for an artist
 * Returns a CSS gradient string that can be used as background-image
 */
export function getPlaceholderCoverGradient(artistId: string): string {
  const index = getPlaceholderIndex(artistId)
  const gradient = COVER_GRADIENTS[index]
  
  return `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.via} 50%, ${gradient.to} 100%)`
}

/**
 * Get placeholder cover image URL for an artist
 * Uses curated Unsplash images for a professional look
 */
export function getPlaceholderCoverUrl(artistId: string): string {
  const index = getPlaceholderIndex(artistId)
  const photoId = COVER_IMAGES[index]
  
  // Unsplash image URL optimized for cover image dimensions
  // 1200x400 matches typical cover aspect ratio
  return `https://images.unsplash.com/${photoId}?w=1200&h=400&fit=crop&crop=center&auto=format&q=80`
}

/**
 * Get all placeholder data for an artist
 */
export function getArtistPlaceholders(artistId: string, artistName?: string) {
  return {
    profileUrl: getPlaceholderProfileUrl(artistId, artistName),
    coverGradient: getPlaceholderCoverGradient(artistId),
    coverUrl: getPlaceholderCoverUrl(artistId),
    index: getPlaceholderIndex(artistId),
  }
}

// Export gradient data for components that want to render gradients directly
export { COVER_GRADIENTS }

